import { useEffect, useState } from 'react';

import * as tracking from './tracking';
import { dispatchCustomEvent, useCustomEvent } from 'client-site-chrome-ui/hooks/useCustomEvent';
import type { Cookies } from 'client-site-chrome-ui/types/cookies';
import type { Locale, Placement } from 'client-site-chrome-ui/types/locale';

const LOCALE_COOKIES = {
  countryCode: 'country_code',
  currency: 'currency',
  deliveryZone: 'delivery_zone',
  countryName: 'country_name',
};

const DEFAULT_LOCALE = {
  currency: 'GBP',
  countryCode: 'GB',
  deliveryZone: 'MAINLAND_UK',
  countryName: 'United Kingdom',
};

interface UseLocaleParams {
  cookies?: Cookies;
}

const SET_LOCALE_EVENT = 'SET_LOCALE_EVENT';

export const useLocale = ({
  cookies,
}: UseLocaleParams): [Locale, (locale: Locale, placement: Placement) => void] => {
  const [locale, setLocale] = useState<Locale>(DEFAULT_LOCALE);
  const [localeUpdated, setLocaleUpdated] = useState(false);

  useEffect(() => {
    if (cookies) {
      const countryCode = cookies.get(LOCALE_COOKIES.countryCode) || DEFAULT_LOCALE.countryCode;
      const currency = cookies.get(LOCALE_COOKIES.currency) || DEFAULT_LOCALE.currency;
      const deliveryZone = cookies.get(LOCALE_COOKIES.deliveryZone) || DEFAULT_LOCALE.deliveryZone;
      const countryName = cookies.get(LOCALE_COOKIES.countryName) || DEFAULT_LOCALE.countryName;

      setLocale({
        currency,
        countryCode,
        deliveryZone,
        countryName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
    handle updates using custom event to enable components rendered in different contexts to communicate.
    This is only need for the mononoth integration.
  */
  useCustomEvent(SET_LOCALE_EVENT, (evt: Event) => setLocale((evt as CustomEvent).detail));

  useEffect(() => {
    if (localeUpdated && cookies) {
      cookies.set(LOCALE_COOKIES.currency, locale.currency);
      cookies.set(LOCALE_COOKIES.countryCode, locale.countryCode);
      cookies.set(LOCALE_COOKIES.deliveryZone, locale.deliveryZone);
      cookies.set(LOCALE_COOKIES.countryName, locale.countryName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  return [
    locale,
    (updatedLocale: Locale, placement: Placement) => {
      setLocaleUpdated(true);
      tracking.sendChangeLocaleEvent(locale, updatedLocale, placement);
      dispatchCustomEvent(SET_LOCALE_EVENT, updatedLocale);
    },
  ];
};
